import React from "react";
import { makeStyles } from "@mui/styles";
import Typography from "@mui/material/Typography";
import CheckIcon from "@mui/icons-material/Check";
import { getMapImageUrl } from "../../constants/mapMeta";
import { formatMapName, resolveMatchMapIdFromSelectionKey } from "../../constants/maps";

const useStyles = makeStyles((theme) => ({
  root: {
    display: "flex",
    alignItems: "center",
    height: "3.25rem",
    borderRadius: "0.5rem",
    border: "solid 1px rgba(255, 255, 255, 0.1)",
    cursor: "pointer",
    transition:
      "background-color 0.2s ease, border-color 0.2s ease, transform 0.2s ease, box-shadow 0.2s ease",
    backgroundColor: "rgba(28, 30, 32, 0.8)",
    // Avoid per-card backdrop-filter here: with hover transforms on macOS it can trigger full-screen repaints/flicker.
    overflow: "hidden",
    position: "relative",
    willChange: "transform",
    "&:hover": {
      backgroundColor: "rgba(232, 163, 61, 0.15)",
      border: "solid 1px rgba(232, 163, 61, 0.4)",
      transform: "translateY(-2px)",
      boxShadow: "0 8px 25px rgba(232, 163, 61, 0.2)",
    },
    [theme.breakpoints.up("xl")]: {
      height: "3.9rem",
    },
  },
  rootSelected: {
    backgroundColor: "rgba(232, 163, 61, 0.2)",
    border: "solid 1px rgba(232, 163, 61, 0.6)",
    "&:hover": {
      backgroundColor: "rgba(232, 163, 61, 0.25)",
      border: "solid 1px rgba(232, 163, 61, 0.8)",
      transform: "translateY(-2px)",
      boxShadow: "0 8px 25px rgba(232, 163, 61, 0.3)",
    },
  },
  rootUnselected: {
    filter: "blur(1px)",
    opacity: 0.7,
    "&:hover": {
      filter: "blur(0px)",
      opacity: 1,
      backgroundColor: "rgba(232, 163, 61, 0.1)",
      border: "solid 1px rgba(232, 163, 61, 0.3)",
      transform: "translateY(-1px)",
      boxShadow: "0 4px 15px rgba(232, 163, 61, 0.15)",
    },
  },
  mapImage: {
    width: "4.8rem",
    height: "100%",
    borderRadius: "0 0 0 0.5rem",
    backgroundSize: "cover",
    backgroundPosition: "center",
    backgroundRepeat: "no-repeat",
    flexShrink: 0,
    position: "relative",
    "&::after": {
      content: '""',
      position: "absolute",
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      background: "linear-gradient(90deg, rgba(0, 0, 0, 0.3) 0%, transparent 50%)",
      pointerEvents: "none",
    },
    [theme.breakpoints.up("xl")]: {
      width: "5.8rem",
    },
  },
  mapNameContainer: {
    flex: 1,
    display: "flex",
    alignItems: "center",
    padding: "0 0.7rem",
    position: "relative",
    zIndex: 2,
  },
  mapName: {
    color: "#ffffff",
    fontSize: "0.95rem",
    textShadow: "0 2px 4px rgba(0, 0, 0, 0.8)",
    margin: 0,
    letterSpacing: "0.5px",
    [theme.breakpoints.up("xl")]: {
      fontSize: "1.08rem",
    },
  },
  mapCheckbox: {
    width: "1.15rem",
    height: "1.15rem",
    backgroundColor: "#393939",
    borderRadius: "0.35rem",
    padding: "0.25rem",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    transition: "all 0.2s ease-in-out",
    flexShrink: 0,
    boxSizing: "border-box",
    marginRight: "0.7rem",
    [theme.breakpoints.up("xl")]: {
      width: "1.35rem",
      height: "1.35rem",
      marginRight: "0.9rem",
    },
  },
  mapCheckboxSelected: {
    backgroundColor: "#393939",
  },
  checkIcon: {
    color: "#ffffff",
    fontSize: "0.78rem",
    fontWeight: "bold",
    [theme.breakpoints.up("xl")]: {
      fontSize: "0.9rem",
    },
  },
}));

export default function MapCard({ name, mapsSelected = [], onMapClicked, disabled = false }) {
  const classes = useStyles();
  const selectionKey = name.toLowerCase().replace(/\s+/g, "_");
  const mapId = resolveMatchMapIdFromSelectionKey(selectionKey);
  const imageUrl = getMapImageUrl(mapId);
  const displayName = formatMapName(name);
  const checked = mapsSelected.includes(selectionKey);

  return (
    <div 
      className={`${classes.root} ${checked ? classes.rootSelected : ""}`} 
      onClick={disabled ? undefined : () => onMapClicked(selectionKey)}
      style={{ pointerEvents: disabled ? 'none' : 'auto' }}
    >
      <div 
        className={classes.mapImage}
        style={{ 
          backgroundImage: imageUrl ? `url(${imageUrl})` : "none",
          backgroundColor: imageUrl ? undefined : "rgba(255, 255, 255, 0.06)",
        }}
      />
      <div className={classes.mapNameContainer}>
        <Typography className={classes.mapName}>{displayName}</Typography>
      </div>
      <div className={`${classes.mapCheckbox} ${checked ? classes.mapCheckboxSelected : ""}`}>
        {checked && <CheckIcon className={classes.checkIcon} />}
      </div>
    </div>
  );
}